import useHitarea from '@packages/systems/ui/hooks/useHitarea';
import {isBoolean} from 'lodash';
import {type CheckboxProps} from './Checkbox.types';

const getHoverState = (
  disabled: boolean,
  hover: undefined | boolean,
  checkboxHover: undefined | boolean
) => {
  if (disabled) {
    return false;
  }
  if (isBoolean(hover)) {
    return hover;
  }
  return checkboxHover;
};

function useHitareaFromProps(props: any) {
  const {
    onBlur,
    onFocus,
    onMouseEnter,
    onMouseOver,
    onMouseLeave,
    onMouseDown,
    onMouseUp,
    onKeyDown,
    onKeyUp,
  } = props;

  return useHitarea({
    onBlur,
    onFocus,
    onMouseEnter,
    onMouseOver,
    onMouseLeave,
    onMouseDown,
    onMouseUp,
    onKeyDown,
    onKeyUp,
  });
}

export function useCheckbox(props: CheckboxProps) {
  const {
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'data-automation-id': dataAutomationId,
    checked,
    dashed,
    defaultChecked,
    disabled = false,
    hover: hoverProp,
    id,
    indeterminate: indeterminateProp,
    intent,
    keyboardFocus,
    label,
    name,
    onChange,
    showPreviewCheck,
    sizing = 'medium',
    style,
    ...rest
  } = props;

  const [hitState, hitHandlers] = useHitareaFromProps(rest);
  const indeterminate = dashed || indeterminateProp;
  const {onBlur, onFocus, ...eventHandlers} = hitHandlers;

  const hover = getHoverState(disabled, hoverProp, hitState.hover);
  const sizingValue = 'medium';

  const checkmarkProps = {
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-checked': Boolean(checked),
    checked: Boolean(checked),
    dashed: indeterminate,
    isDisabled: disabled, // Prevents it from rendering into the HTML element
    hover,
    indeterminate,
    intent,
    keyboardFocus: keyboardFocus || hitState.keyboardFocus,
    label,
    role: 'checkbox',
    showPreviewCheck,
    sizing: sizingValue,
  } as const;

  const inputProps = {
    ...hitState,
    /**
     * Currently (aria) hiding the `input` element. This is to prevent
     * voiceover from confusing the 2 `checkbox` elements in this component
     * as a group.
     *
     * For the HTML `input`, we're only using it for native
     * state and interaction handling (e.g. `onChange`).
     *
     * Instead, we're using a `<CheckboxCheckmark />` as the visible and
     * rendered `checkbox`.
     */
    'aria-hidden': true,
    // https://stackoverflow.com/questions/5985839/bug-with-firefox-disabled-attribute-of-input-not-resetting-when-refreshing
    autoComplete: 'off',
    'data-automation-id': dataAutomationId,
    'data-indeterminate': indeterminate,
    checked,
    defaultChecked,
    disabled,
    id,
    indeterminate,
    name,
    onBlur,
    onChange,
    onFocus,
    sizing,
    type: 'checkbox',
  } as const;

  const labelProps = {
    isDisabled: disabled, // Prevents it from rendering into the HTML element
    hover,
    sizing,
  } as const;

  return {
    ...rest,
    ...eventHandlers,
    checkmarkProps,
    inputProps,
    label,
    labelProps,
    style,
  };
}
